import { createContext, useEffect } from "react";
import { useParams } from "react-router-dom";

import { usePart } from "#modules/parts/view/usePart";
import { getDefaultFiltersParams } from "#modules/quoteRequestLines/infra/getDefaultFiltersParams";
import { useQuoteRequestLine } from "#modules/quoteRequestLines/view/useQuoteRequestLine";
import { getIsEmbeddingLoading } from "#modules/similarPart/domain/isEmbeddingLoading";
import { useSearchQuoteRequestLineSimilarParts } from "#modules/similarPart/infra/useSearchQuoteRequestLineSimilarParts";
import { ClusterCard } from "#modules/similarPart/view/clustering/ClusterCard";
import { SidePartCard } from "#modules/similarPart/view/clustering/SidePartCard";
import { EmbeddingErrorCheck } from "#modules/similarPart/view/errors/EmbeddingErrorCheck";
import { SearchingStatusCheck } from "#modules/similarPart/view/errors/SearchingStatusCheck";
import { SimilarPartsErrorCheck } from "#modules/similarPart/view/errors/SimilarPartsErrorCheck";
import { Filters } from "#modules/similarPart/view/filters/Filters";
import { useFilters } from "#modules/similarPart/view/filters/useFilters";
import { PAGE_SIZE, SearchResult } from "#modules/similarPart/view/SearchResult";
import { useCheckPart } from "#modules/similarPart/view/useCheckPart";
import { useDataChunkSize } from "#shared/helpers/useDataChunkSize";
import { Flex } from "#shared/view/components/Flex";
import { Group } from "#shared/view/components/Group";
import { Loader } from "#shared/view/components/Loader";
import { Stack } from "#shared/view/components/Stack";
import { Text } from "#shared/view/components/Text";

type ClusterPageContextType = {
  refetchData: () => void;
};

const ClusterPageContext = createContext<ClusterPageContextType>({
  refetchData: () => {},
});

export const ClusterPage = () => {
  const { quoteRequestLineId } = useParams<{ quoteRequestLineId: string }>();
  const { quoteRequestLine } = useQuoteRequestLine({
    id: quoteRequestLineId,
  });

  const { filtersParams, setFiltersParams } = useFilters();

  useEffect(() => {
    const defaultFiltersParams = getDefaultFiltersParams(quoteRequestLine);

    setFiltersParams(defaultFiltersParams);
  }, [setFiltersParams, quoteRequestLine]);

  const { checkPart } = useCheckPart();

  const { part, isLoading: isLoadingPart } = usePart(quoteRequestLine?.part.id, {
    refetchInterval: (query) => (getIsEmbeddingLoading(query.state.data) ? 2000 : false),
  });

  const { dataChunkSize: limit } = useDataChunkSize(PAGE_SIZE);

  const {
    similarParts,
    isLoading: isFiltering,
    isFetching,
    refetch,
  } = useSearchQuoteRequestLineSimilarParts(
    {
      id: part?.id,
      filtersParams,
      limit,
    },
    {
      enabled: !!part && !!part.id,
    },
  );

  const hasDimensionsFilter = !!part?.length && !!part.width && !!part.height;

  if (isLoadingPart) {
    return <Loader />;
  }

  return (
    <ClusterPageContext.Provider value={{ refetchData: refetch }}>
      <Flex gap={"md"} p={"xs"}>
        <SidePartCard
          sourceQuoteRequestLine={quoteRequestLine}
          onSubmitPartDimensions={refetch}
          FooterComponent={() => (
            <Filters
              filtersParams={filtersParams}
              onUpdateFilters={setFiltersParams}
              onSearch={refetch}
              hasDimensionsFilter={hasDimensionsFilter}
            />
          )}
        />

        <Stack w={"100%"} gap={"xxs"}>
          <Stack gap={0}>
            <Text fz="h1" fw={600}>
              Pièces similaires trouvées
            </Text>
            <Text>Sélectionnez les pièces similaires qui serviront à l’estimation</Text>
          </Stack>

          <EmbeddingErrorCheck part={part ?? null} checkPart={checkPart}>
            <SearchingStatusCheck isSearching={isFiltering}>
              <SimilarPartsErrorCheck similarParts={similarParts ?? null}>
                <SearchResult
                  sourcePart={part}
                  similarParts={similarParts}
                  columnCount={5}
                  sourceQuoteRequestLine={quoteRequestLine}
                  Card={ClusterCard}
                >
                  <Group>
                    <SearchResult.MoreButton isFetching={isFetching} variant="outline" />
                    <SearchResult.CompareButton style={{ fill: "white", marginLeft: "auto" }} />
                  </Group>
                </SearchResult>
              </SimilarPartsErrorCheck>
            </SearchingStatusCheck>
          </EmbeddingErrorCheck>
        </Stack>
      </Flex>
    </ClusterPageContext.Provider>
  );
};
